import { useEffect, useState } from 'react';
import type { AircraftPhoto, FlightAirport, FlightPlanResponse } from '@flightscanner/shared';
import { formatAirportDetail, formatAirportLabel } from '@flightscanner/shared';
import type { RichAircraft } from '../data/enrich';
import { fetchFlightPlan, fetchPhoto } from '../data/apiClient';
import { useStore } from '../store';
import { fmtAlt, fmtSpeed, fmtHeading, fmtVertRate, fmtSeen, compassLabel, cls } from '../lib/format';
import { isQuickLookSupported, openQuickLook } from '../ar/quickLook';
import { aircraftIconDataUrl, isEmergency, resolveAircraftIconKind } from '../map/aircraftIcons';

interface Props {
  aircraft: RichAircraft | null;
}

const ICON_LABELS: Record<string, string> = {
  presidential: 'Presidential / VIP',
  government: 'Government',
  emergency: 'Emergency',
  'military-fighter': 'Military fighter',
  'military-transport': 'Military transport',
  'military-other': 'Military aircraft',
  'military-helicopter': 'Military helicopter',
  helicopter: 'Helicopter',
  balloon: 'Balloon',
  'heavy-commercial': 'Heavy commercial',
  commercial: 'Commercial jet',
  regional: 'Regional jet',
  turboprop: 'Turboprop',
  'private-jet': 'Private jet',
  'light-prop': 'Light aircraft',
  glider: 'Glider',
  unknown: 'Unknown aircraft',
};

export function AircraftPanel({ aircraft }: Props) {
  const open = useStore((s) => s.panelOpen);
  const setOpen = useStore((s) => s.setPanelOpen);
  const setArMode = useStore((s) => s.setArMode);
  const [photo, setPhoto] = useState<AircraftPhoto | null>(null);
  const [photoReady, setPhotoReady] = useState(false);
  const [photoFailed, setPhotoFailed] = useState(false);
  const [flightPlan, setFlightPlan] = useState<FlightPlanResponse | null>(null);
  const [qlSupported] = useState(() => isQuickLookSupported());

  useEffect(() => {
    setPhoto(null);
    setPhotoReady(false);
    setPhotoFailed(false);
    if (!aircraft) return;
    const ctrl = new AbortController();
    fetchPhoto(aircraft.icao, {
      type: aircraft.type ?? aircraft.typeInfo?.icao ?? null,
      reg: aircraft.registration,
      description: [aircraft.description, aircraft.typeInfo?.model].filter(Boolean).join(' · ') || null,
      signal: ctrl.signal,
    })
      .then(setPhoto)
      .catch(() => {})
      .finally(() => setPhotoReady(true));
    return () => ctrl.abort();
  }, [aircraft?.icao, aircraft?.type, aircraft?.registration, aircraft?.description, aircraft?.typeInfo?.icao, aircraft?.typeInfo?.model]);

  useEffect(() => {
    setFlightPlan(null);
    if (!aircraft) return;
    const ctrl = new AbortController();
    fetchFlightPlan(aircraft.icao, {
      callsign: aircraft.callsign,
      lat: aircraft.lat,
      lon: aircraft.lon,
      military: aircraft.flightClass === 'military' || aircraft.isMilitary,
      operatorCountry: aircraft.airline?.country ?? aircraft.inferredCountry,
      signal: ctrl.signal,
    })
      .then(setFlightPlan)
      .catch(() => {
        setFlightPlan({
          icao: aircraft.icao,
          callsign: aircraft.callsign,
          departure: null,
          arrival: null,
          routeKnown: false,
          departureInferred: false,
          fromCache: false,
        });
      });
    return () => ctrl.abort();
  }, [aircraft?.icao, aircraft?.callsign, aircraft?.lat, aircraft?.lon, aircraft?.flightClass, aircraft?.isMilitary, aircraft?.airline?.country, aircraft?.inferredCountry]);

  if (!aircraft) return null;
  const airline = aircraft.airline;
  const typeInfo = aircraft.typeInfo;
  const emergency = aircraft.squawk && ['7700', '7600', '7500'].includes(aircraft.squawk);
  const iconLabel = ICON_LABELS[aircraft.iconKind] ?? aircraft.iconKind;

  return (
    <>
      {open && <div className="fixed inset-0 z-40 bg-black/30 sm:hidden" onClick={() => setOpen(false)} />}
      <section
        className={cls(
          'fixed right-0 z-50 flex w-full flex-col overflow-hidden border-l border-bg-border bg-bg-panel/95 backdrop-blur transition-transform duration-200',
          'bottom-0 h-[68vh] max-h-[68vh] rounded-t-2xl sm:top-0 sm:h-full sm:max-h-full sm:max-w-md sm:rounded-none',
          open ? 'translate-y-0 sm:translate-x-0' : 'translate-y-full sm:translate-y-0 sm:translate-x-full',
        )}
        style={{ paddingBottom: 'var(--safe-bottom)' }}
      >
        <div className="flex shrink-0 items-center justify-between px-4 pt-3">
          <div className="mx-auto h-1.5 w-10 rounded-full bg-bg-border sm:hidden" />
          <button onClick={() => setOpen(false)} className="ml-auto rounded-md bg-bg-raised px-2.5 py-1 text-xs text-slate-300">
            Close
          </button>
        </div>

        <div className="scroll-thin min-h-0 flex-1 overflow-y-auto overscroll-contain touch-pan-y">
          <div className="px-4 pb-6">
            <div className="mt-1 flex items-center gap-3">
              <AircraftTypeIcon aircraft={aircraft} />
              <div className="min-w-0">
                <div className="truncate text-lg font-semibold text-slate-100">
                  {aircraft.callsign ?? aircraft.registration ?? aircraft.icao.toUpperCase()}
                </div>
                <div className="truncate text-sm text-slate-400">
                  {airline?.name ?? (aircraft.flightClass === 'military' ? 'Military' : 'Unknown operator')}
                </div>
              </div>
            </div>

            {photo?.found && photo.thumbnailUrl && !photoFailed ? (
              <div className="mt-3">
                <AircraftImage
                  src={photo.thumbnailUrl}
                  alt={photo.representative ? photo.label ?? aircraft.type ?? 'Aircraft' : aircraft.registration ?? aircraft.icao}
                  onFailed={() => setPhotoFailed(true)}
                />
                {photo.representative ? (
                  <p className="mt-1.5 text-[11px] text-slate-500">
                    Representative {photo.label ?? aircraft.type} photo
                    {photo.attribution ? <> · {photo.attribution}</> : null}
                  </p>
                ) : photo.photographer ? (
                  <p className="mt-1.5 text-[11px] text-slate-500">
                    Photo: {photo.photographer}
                    {photo.fullUrl ? (
                      <>
                        {' '}
                        ·{' '}
                        <a href={photo.fullUrl} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline">
                          {photo.source === 'jetphotos' ? 'JetPhotos' : 'planespotters'}
                        </a>
                      </>
                    ) : null}
                  </p>
                ) : null}
              </div>
            ) : photoReady ? (
              <AircraftPhotoFallback aircraft={aircraft} />
            ) : (
              <div className="mt-3 h-40 w-full animate-pulse rounded-lg bg-bg-raised" aria-hidden />
            )}

            {emergency && (
              <div className="mt-3 rounded-md bg-bad/20 px-3 py-2 text-sm text-red-300 ring-1 ring-bad/50">
                Emergency squawk {aircraft.squawk}
              </div>
            )}

            {aircraft.specialKind && (
              <div className="mt-3 rounded-md bg-yellow-400/15 px-3 py-2 text-sm text-yellow-200 ring-1 ring-yellow-300/40">
                Enhanced tracking active: full selected-flight history and highlighted route.
              </div>
            )}

            <div className="mt-4 grid grid-cols-2 gap-2">
              <Stat label="Altitude" value={fmtAlt(aircraft.altBaro)} />
              <Stat label="Speed" value={fmtSpeed(aircraft.groundSpeed)} />
              <Stat label="Heading" value={`${fmtHeading(aircraft.track)} ${compassLabel(aircraft.track ?? 0)}`} />
              <Stat label="Vertical" value={fmtVertRate(aircraft.verticalRate)} />
              <Stat label="Squawk" value={aircraft.squawk ?? '—'} />
              <Stat label="Last seen" value={fmtSeen(aircraft.seen)} />
            </div>

            <dl className="mt-4 space-y-1.5 text-sm">
              <RouteRow
                label="Takeoff"
                airport={flightPlan?.departure}
                inferred={flightPlan?.departureInferred}
                pending={flightPlan === null}
              />
              <RouteRow label="Destination" airport={flightPlan?.arrival} pending={flightPlan === null} />
              <Row
                k="Type"
                v={typeInfo ? `${typeInfo.manufacturer} ${typeInfo.model}` : aircraft.description ?? aircraft.type ?? '—'}
              />
              <Row k="Registration" v={aircraft.registration ?? '—'} />
              <Row k="Operator" v={aircraft.operator ?? airline?.name ?? '—'} />
              <Row k="ICAO hex" v={aircraft.icao.toUpperCase()} />
              <Row k="Country" v={aircraft.inferredCountry ?? '—'} />
              <Row k="Category" v={iconLabel} />
              <Row k="On ground" v={aircraft.onGround ? 'yes' : 'no'} />
            </dl>

            <div className="mt-5 grid grid-cols-2 gap-2">
              <button
                onClick={() => {
                  setArMode('sky-hud');
                  setOpen(false);
                }}
                className="rounded-lg bg-accent px-3 py-2.5 text-sm font-medium text-bg"
              >
                View in Sky AR
              </button>
              <button
                onClick={() => openQuickLook('/models/plane.usdz')}
                disabled={!qlSupported}
                className={cls(
                  'rounded-lg px-3 py-2.5 text-sm font-medium ring-1',
                  qlSupported ? 'bg-bg-raised text-slate-100 ring-bg-border' : 'bg-bg-panel text-slate-600 ring-bg-border',
                )}
                title={qlSupported ? 'Open 3D model in AR Quick Look' : 'AR Quick Look requires iOS Safari'}
              >
                3D model{qlSupported ? '' : ' (iOS)'}
              </button>
            </div>
            <p className="mt-2 text-[11px] text-slate-500">
              Sky AR uses your camera + compass to label planes overhead. 3D model opens Apple AR Quick Look.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-bg-raised px-3 py-2">
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="font-mono text-sm text-slate-100">{value}</div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex items-center justify-between">
      <dt className="text-slate-500">{k}</dt>
      <dd className="text-slate-200">{v}</dd>
    </div>
  );
}

function RouteRow({
  label,
  airport,
  inferred,
  pending,
}: {
  label: string;
  airport: FlightAirport | null | undefined;
  inferred?: boolean;
  pending?: boolean;
}) {
  if (pending) return <Row k={label} v="—" />;
  const main = formatAirportLabel(airport);
  const detail = formatAirportDetail(airport);
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-right text-slate-200">
        <div>
          {main}
          {inferred && main !== 'N/A' ? <span className="text-slate-500"> (inferred)</span> : null}
        </div>
        {detail && main !== 'N/A' ? <div className="text-xs text-slate-500">{detail}</div> : null}
      </dd>
    </div>
  );
}

/**
 * Panel photo with automatic retry. External photo CDNs (JetPhotos /
 * planespotters / Wikimedia) occasionally drop the first load; remounting the
 * <img> via a key change forces a reload. After exhausting retries we give up
 * so the caller can fall back to the aircraft icon.
 */
function AircraftImage({ src, alt, onFailed }: { src: string; alt: string; onFailed: () => void }) {
  const [reloads, setReloads] = useState(0);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setReloads(0);
    setFailed(false);
  }, [src]);

  const handleError = () => {
    if (reloads < 2) {
      const next = reloads + 1;
      window.setTimeout(() => setReloads(next), 350 * next);
    } else {
      setFailed(true);
      onFailed();
    }
  };

  if (failed) return null;

  return (
    <img
      key={reloads}
      src={src}
      alt={alt}
      loading="eager"
      decoding="async"
      referrerPolicy="no-referrer"
      onError={handleError}
      className="w-full rounded-lg object-cover"
    />
  );
}

function AircraftPhotoFallback({ aircraft }: { aircraft: RichAircraft }) {
  const emergency = isEmergency(aircraft);
  const kind = resolveAircraftIconKind(aircraft);
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setSrc(null);
    aircraftIconDataUrl(kind, emergency).then((url) => {
      if (!cancelled) setSrc(url);
    });
    return () => {
      cancelled = true;
    };
  }, [kind, emergency]);

  if (!src) {
    return <div className="mt-3 h-36 w-full rounded-lg bg-bg-raised" aria-hidden />;
  }

  return (
    <div className="mt-3 flex h-36 w-full items-center justify-center rounded-lg bg-bg-raised">
      <img src={src} alt="" className="h-24 w-24 object-contain opacity-80" aria-hidden />
    </div>
  );
}

function AircraftTypeIcon({ aircraft }: { aircraft: RichAircraft }) {
  const emergency = isEmergency(aircraft);
  const kind = resolveAircraftIconKind(aircraft);
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setSrc(null);
    aircraftIconDataUrl(kind, emergency).then((url) => {
      if (!cancelled) setSrc(url);
    });
    return () => {
      cancelled = true;
    };
  }, [kind, emergency]);

  if (!src) {
    return <div className="h-12 w-12 shrink-0 rounded-md bg-bg-raised" aria-hidden />;
  }

  return <img src={src} alt="" className="h-12 w-12 shrink-0 object-contain" aria-hidden />;
}
